import {
  FILTERED,
  FluidEditableCellConfig,
  FluidExportOptions,
  FluidFormElementState,
  FluidKeyedObject,
  FluidMutationUpdate,
  FluidRowData,
  FluidRowMutation,
  FluidTableHeader,
  FluidTableRowData,
  FluidTableRowMetadata,
  getAtPath,
  isNullOrUndefined,
  SEARCHED,
  setAtPath,
  VISIBLE,
} from '@lmig/fluid-core';
import { forceUpdate } from '@stencil/core';
import { timer } from 'rxjs';

import { DATA_KEYS, ID, SELECTED } from '../../../constants/constants';
import { DataItem } from '../../../model/typings/fluid-generic.type';
import { Utils } from '../../../utils/utils';
import { FluidTable } from '../fluid-table';
import {
  getDataAtPath,
  getMutatedRowData,
  setDataAtPath,
} from './fluid-table-data-management.utils';
import { sortData } from './fluid-table-sort.utils';

/**
 * Whenever an editable cell is rendered, take its reference and store it against the
 * host so we have a list of editable cells to get value or reset where necessary.
 * @param host
 * @param rowId
 * @param dataPath
 * @param reference
 */
export const addCellReference = (
  host,
  rowId: string,
  dataPath: string,
  reference: HTMLFluidFormElementElement
) => {
  host._editableCells[rowId] = {
    ...host._editableCells[rowId],
    [dataPath]: {
      reference,
    },
  };
};

/**
 * Called whenever there is a change to one of the editable fields in a row.
 * Due to the nature of re-renders and data updates, we only consider a field 'updated'
 * when it is actually touched, so we can keep a track of user interactions, rather than
 * system interactions.
 * @param host
 * @param rowId
 * @param dataPath
 * @param state
 * @param cleared
 */
export const handleCellUpdate = (
  host,
  rowId: string,
  dataPath: string,
  state: FluidFormElementState,
  cleared = false
) => {
  if (isNullOrUndefined(state.value)) {
    state.value = '';
  }

  // Apply output formatting
  const header = host._headers.find(
    (header: FluidTableHeader) => header.dataPath === dataPath
  );
  if (
    header &&
    header.editableCellConfig?.outputFormat &&
    header.editableCellConfig?.outputFormat.applyInline
  ) {
    state.value = header.editableCellConfig?.outputFormat.formatter(
      state.value
    );
  }

  host._editableCells[rowId] = {
    ...host._editableCells[rowId],
    [dataPath]: {
      ...(host._editableCells[rowId]
        ? host._editableCells[rowId][dataPath]
        : {}),
      state,
    },
  };

  if (state.touched || cleared) {
    host._updatedRows[rowId] = {
      ...host._updatedRows[rowId],
      [dataPath]: { rowId, newValue: state.value, dataPath },
    };

    const updatedCell = { ...host._dataSource.find((d) => d.rowId === rowId) };
    host.editableCellChange.emit({
      rowId,
      rowData: stripFluidData(updatedCell),
      value: state,
      actionKey: dataPath,
    });

    if (!host.formActionsConfig?.showEditActions) {
      commitEditableCellChanges(host);
    }

    forceUpdate(host);
    timer(100).subscribe(() => validateRow(host, rowId));
  }
};

/**
 * Validates a single row by rowId
 * @param host
 * @param rowId
 */
export const validateRow = (host, rowId) => {
  getEditableCellReferencesForRow(host._editableCells, rowId).forEach((ref) => {
    ref.markTouchedAndValidate();
  });
};

/**
 * Gets the mutated value from the temporary row mutation storage (used to prevent editable cells resetting during
 * a rerender caused by expanded/collapsing rows)
 * @param updatedRows
 * @param rowId
 * @param dataPath
 */
export const getMutatedValue = (
  updatedRows: FluidKeyedObject<FluidKeyedObject<FluidRowMutation>>,
  rowId: string,
  dataPath: string
) => {
  return updatedRows[rowId]
    ? updatedRows[rowId][dataPath]?.newValue
    : undefined;
};

/**
 * Resets the value of all editable cells to the value currently held in the _dataSource objects.
 * Until the row mutations have been committed to the _dataSource, they remain as they were when
 * first fed to the table, so we use that value as our 'reset'. After they are 'committed' (by clicking
 * the save button), we update _dataSource, and reset will reset the value to the last committed value.
 * @param host
 */
export const resetEditableCells = (host) => {
  const currentUpdatedRows = host._updatedRows;
  host._updatedRows = {};
  if (Object.keys(host._editableCells).length > 0) {
    Object.keys(currentUpdatedRows).forEach((rowId) => {
      Object.keys(currentUpdatedRows[rowId]).forEach((dataPath) => {
        const cellValue = getDataAtPath(
          host._dataSource.find((r) => r.rowId === rowId),
          null,
          dataPath
        );
        host._editableCells[rowId][dataPath]?.reference
          .setValue(cellValue)
          .then(() => {
            host._editableCells[rowId][dataPath]?.reference
              .getValue()
              .then((state) => {
                state.touched = false;
                handleCellUpdate(host, rowId, dataPath, state);
              });
          });
      });
    });
  }
  forceUpdate(host);
};

/**
 * Takes all the stored row mutations, and commits them to the _dataSource object, before emitting the
 * now updated value of the table, and clearing out the _updateRows source (as the values are now committed
 * so are no longer 'updated')
 * @param host
 */
export const commitEditableCellChanges = (host) => {
  Object.keys(host._updatedRows).forEach((rowId) => {
    const dataItemIdx = host._dataSource.findIndex((d) => d.rowId == rowId);
    Object.keys(host._updatedRows[rowId]).forEach((dataPath) => {
      const newValue = host._updatedRows[rowId][dataPath]?.newValue;
      host._dataSource[dataItemIdx] = setDataAtPath(
        host._dataSource[dataItemIdx],
        null,
        newValue,
        dataPath
      );
    });
  });

  if (!host.formActionsConfig?.showEditActions) {
    emitTableUpdates(host);
  }
};

/**
 * Checks overall table validity, before emitting the dataMutated event
 * @param host
 * @param withUpdates
 */
export const emitTableUpdates = <T extends Partial<FluidTable>>(
  host: T,
  withUpdates = true
): void => {
  const emit = () => {
    host.tableIsValid().then((tableIsValid) => {
      host.dataMutated.emit(
        createTableMutationUpdate(host, tableIsValid, withUpdates)
      );
    });
  };
  if (host._dataRefreshing) {
    console.debug(
      `🧪 Table Refreshing - Delaying Emission by ${host.refreshDelay} milliseconds.`
    );
    Utils.delayBy(host.refreshDelay, () => emit());
  } else {
    emit();
  }
};

/**
 * Gets a version of a dataItem with any uncommitted mutations applied.
 * @param host
 * @param dataItem
 */
export const getMutatedDataItem = (
  host: Partial<FluidTable>,
  dataItem: FluidTableRowData<Record<string, unknown>>
) => {
  const updates = host._updatedRows[dataItem[ID]];
  if (updates) {
    Object.keys(updates).forEach((dataPath) => {
      dataItem = setDataAtPath(
        dataItem,
        null,
        updates[dataPath].newValue,
        dataPath
      );
    });
  }
  return dataItem;
};

/**
 * Creates a table mutation update
 * @param host
 * @param tableIsValid
 * @param withUpdates
 */
export const createTableMutationUpdate = (
  host,
  tableIsValid: boolean,
  withUpdates = false
) => {
  const dataSource = withUpdates
    ? host._dataSource.map((ds) => getMutatedRowData(host._updatedRows, ds))
    : host._dataSource;

  const formattedOutput = dataSource.map((dataItem) => {
    host._headers?.forEach((header) => {
      if (
        header.editableCellConfig?.outputFormat &&
        !header.editableCellConfig?.outputFormat.applyInline
      ) {
        dataItem = setAtPath(
          dataItem,
          header.editableCellConfig.outputFormat.formatter(
            getAtPath(dataItem, header.dataPath)
          ),
          header.dataPath
        );
      }
    });
    return dataItem;
  });

  return {
    tableIsValid,
    updatedDataSet: cleanDatasourceForEmit(
      formattedOutput,
      host.retainTransientProperties
    ),
    deletedRows: createDeletedRowList(host._deletedRows),
    modifiedRows: createModifiedRowList(host._updatedRows),
    filteredRows: cleanDatasourceForEmit([
      ...formattedOutput.filter(
        (dataItem: FluidRowData) => dataItem[FILTERED] || dataItem[SEARCHED]
      ),
    ]),
    visibleRows: cleanDatasourceForEmit(
      getVisibleTableRows(host, formattedOutput, host.exportOptions)
    ),
    exportableRows: cleanDatasourceForEmit(
      getVisibleTableRows(host, formattedOutput, host.exportOptions, true)
    ),
    selectedRows: cleanDatasourceForEmit(
      formattedOutput.filter((ds: DataItem) => ds[SELECTED]),
      host.retainTransientProperties
    ),
    unfilteredRows: cleanDatasourceForEmit(
      formattedOutput.filter(
        (dataItem: FluidRowData) => !dataItem[FILTERED] && !dataItem[SEARCHED]
      )
    ),
  } as FluidMutationUpdate;
};

/**
 * Gets the editable cell config from the header if it exists, running the function (if applicable)
 * on the data item.
 * @param header
 * @param dataItem
 */
export function getEditableCellConfig(
  header: FluidTableHeader,
  dataItem: FluidRowData
): FluidEditableCellConfig | undefined {
  let editableCellConfig: FluidEditableCellConfig = header.editableCellConfig;

  if (typeof editableCellConfig === 'function') {
    editableCellConfig = (
      editableCellConfig as (rowData: FluidRowData) => FluidEditableCellConfig
    )(dataItem);
  }

  if (!editableCellConfig) return undefined;
  return editableCellConfig;
}

/**
 * Gets a current snapshot of the data in the table, ready for export.
 * Includes any sorting, filtering or pagination (optional) applied.
 * @param host
 * @param dataSource
 * @param exportOptions
 * @param exportableOnly - if true, will return all unfiltered, even if not on current page
 */
export const getVisibleTableRows = (
  host: FluidTable,
  dataSource: FluidTableRowData<any>[],
  exportOptions: FluidExportOptions,
  exportableOnly = false
) => {
  let pagedDataSource = exportOptions?.pageExport
    ? dataSource.filter((dataItem) => dataItem[VISIBLE])
    : dataSource;
  const sortState = host.tableStateStore?.getSortState();
  if (sortState) {
    pagedDataSource = sortData(
      pagedDataSource,
      sortState.sortDirection,
      sortState.header.dataPath,
      sortState.header.customSort?.[sortState.sortDirection]
    );
  }
  return pagedDataSource.filter((dataItem: FluidTableRowData<any>) =>
    exportableOnly
      ? !dataItem[FILTERED]
      : !dataItem[FILTERED] && dataItem[VISIBLE]
  );
};

/**
 * Converts a map of modified rows and dataPaths to an array of row mutations.
 * @param modifiedRows
 */
export const createModifiedRowList = (
  modifiedRows: Record<string, Record<string, FluidRowMutation>>
) => {
  return Object.keys(modifiedRows).reduce((items, key: string) => {
    return [
      ...items,
      ...Object.keys(modifiedRows[key]).map(
        (dataPath) => modifiedRows[key][dataPath]
      ),
    ];
  }, []);
};

/**
 * Converts a map of deleted rows to an array of row mutations
 * @param deletedRows
 */
export const createDeletedRowList = (
  deletedRows: Record<string, FluidTableRowData<Record<string, unknown>>>
) => {
  return Object.values(deletedRows);
};

/**
 * Adds the deleted rows back into the data source and clears the deleted rows
 * memory store.
 * @param host
 * @param deletedRows
 */
export const undoDeletedRows = (host, deletedRows) => {
  host._dataSource = [...host._dataSource, ...deletedRows];
  host._deletedRows = {};
};

/**
 * Strips any FLUID added properties on the dataItems before emitting it to
 * the parent.
 * @param dataSource
 * @param retainedProperties
 */
export const cleanDatasourceForEmit = (
  dataSource: FluidTableRowData<Record<string, unknown>>[],
  retainedProperties: (keyof FluidTableRowMetadata)[] = []
) => {
  const newArray = [];
  (dataSource || []).forEach((ds) => newArray.push({ ...ds }));
  return newArray.map((ds) => stripFluidData(ds, retainedProperties));
};

/**
 * Strips any FLUID added properties on a single dataItem
 * @param dataItem
 * @param retainedProperties
 */
export const stripFluidData = (
  dataItem: FluidTableRowData<Record<string, unknown>>,
  retainedProperties: (keyof FluidTableRowMetadata)[] = []
) => {
  DATA_KEYS.forEach((key) => {
    if (retainedProperties.indexOf(key) === -1) {
      delete dataItem[key];
    }
  });
  return dataItem;
};

/**
 * Converts a map of editable cells and dataPaths to an array of form element references.
 * @param editableCells
 */
export const getEditableCellReferences = (
  editableCells: FluidKeyedObject<
    FluidKeyedObject<{ reference: HTMLFluidFormElementElement }>
  >
) => {
  return Object.keys(editableCells).reduce((items, key) => {
    return [
      ...items,
      ...Object.keys(editableCells[key]).map(
        (dataPath) => editableCells[key][dataPath]
      ),
    ];
  }, []);
};

/**
 * Converts a map of editable cells and dataPaths to an array of form element references for a given rowId.
 * @param editableCells
 * @param rowId
 */
export const getEditableCellReferencesForRow = (
  editableCells: FluidKeyedObject<
    FluidKeyedObject<{ reference: HTMLFluidFormElementElement }>
  >,
  rowId: string
) => {
  return Object.keys(editableCells[rowId]).reduce((items, key) => {
    return [...items, editableCells[rowId][key]?.reference];
  }, []);
};

/**
 * Gets references to all the editable cells, and returns the state of each in an array.
 * @param editableCells
 */
export const getEditableCellStates = (
  editableCells: FluidKeyedObject<
    FluidKeyedObject<{ reference: HTMLFluidFormElementElement }>
  >
) => {
  return getEditableCellReferences(editableCells).map((cellRef) => {
    return cellRef.reference?.getValue();
  });
};
